import { nanoid } from '@reduxjs/toolkit';
import { create } from 'zustand';
import type { ToolExecution, ToolExecutionState } from '../components/ToolExecutionIndicator.js';

interface ToolExecutionStore extends ToolExecutionState {
  // Actions
  startToolExecution: (toolName: string, args?: any, category?: 'backend' | 'frontend') => string;
  updateToolStatus: (toolId: string, status: ToolExecution['status'], result?: any, error?: string) => void;
  completeToolExecution: (toolId: string, result?: any) => void;
  failToolExecution: (toolId: string, error: string) => void;
  setAgentThinking: (thinking: string | undefined) => void;
  clearCompleted: () => void;
  reset: () => void;
}

export const useToolExecutionStore = create<ToolExecutionStore>((set, get) => ({
  // Initial state
  isExecuting: false,
  currentTools: [],
  completedTools: [],
  agentThinking: undefined,
  
  // Actions
  startToolExecution: (toolName, args, category = 'frontend') => {
    const toolId = nanoid();
    const tool: ToolExecution = {
      id: toolId,
      toolName,
      args,
      status: 'pending',
      timestamp: new Date(),
      category,
    };
    
    set((state) => ({
      isExecuting: true,
      currentTools: [...state.currentTools, tool],
    }));
    
    // Auto-mark as executing after a short delay
    setTimeout(() => {
      get().updateToolStatus(toolId, 'executing');
    }, 100);
    
    return toolId;
  },
  
  updateToolStatus: (toolId, status, result, error) => {
    set((state) => ({
      currentTools: state.currentTools.map((tool) =>
        tool.id === toolId
          ? { ...tool, status, result, error }
          : tool
      ),
    }));
  },
  
  completeToolExecution: (toolId, result) => {
    set((state) => {
      const tool = state.currentTools.find((t) => t.id === toolId);
      if (!tool) return state;
      
      const updatedTool = {
        ...tool,
        status: 'success' as const,
        result,
      };
      
      const newCurrentTools = state.currentTools.filter((t) => t.id !== toolId);
      
      return {
        currentTools: newCurrentTools,
        completedTools: [...state.completedTools, updatedTool],
        isExecuting: newCurrentTools.length > 0,
      };
    });
  },
  
  failToolExecution: (toolId, error) => {
    set((state) => {
      const tool = state.currentTools.find((t) => t.id === toolId);
      if (!tool) return state;
      
      const updatedTool = {
        ...tool,
        status: 'error' as const,
        error,
      };
      
      const newCurrentTools = state.currentTools.filter((t) => t.id !== toolId);
      
      return {
        currentTools: newCurrentTools,
        completedTools: [...state.completedTools, updatedTool],
        isExecuting: newCurrentTools.length > 0,
      };
    });
  },
  
  setAgentThinking: (thinking) => {
    set({ agentThinking: thinking });
  },
  
  clearCompleted: () => {
    set({ completedTools: [] });
  },
  
  reset: () => {
    set({
      isExecuting: false,
      currentTools: [],
      completedTools: [],
      agentThinking: undefined,
    });
  },
}));

// Helper hook to track tool execution from agent responses
export function useTrackToolExecution() {
  const {
    startToolExecution,
    completeToolExecution,
    failToolExecution,
    setAgentThinking,
  } = useToolExecutionStore();
  
  const trackToolCall = (toolCall: any) => {
    const category = isBackendTool(toolCall.toolName) ? 'backend' : 'frontend';
    const toolId = startToolExecution(toolCall.toolName, toolCall.args, category);
    return toolId;
  };
  
  const trackToolResult = (toolId: string, result: any, error?: string) => {
    if (error) {
      failToolExecution(toolId, error);
    } else {
      completeToolExecution(toolId, result);
    }
  };
  
  const trackAgentMessage = (message: any) => {
    // Extract thinking from the message content if it contains specific patterns
    if (message.content) {
      const thinkingMatch = message.content.match(/(?:plan|planning|preamble|thinking|analyzing|considering)[:.]?\s*(.+?)(?:\.|$)/i);
      if (thinkingMatch) {
        setAgentThinking(thinkingMatch[1]);
      }
    }
    
    // Track tool calls
    if (message.toolCalls && message.toolCalls.length > 0) {
      const toolIds: Record<string, string> = {};
      message.toolCalls.forEach((toolCall: any) => {
        // Attach a minimal preamble if present in content text
        // Expect models to include brief preambles via system prompt instruction
        if (typeof message.content === 'string' && message.content.length > 0) {
          toolCall.args = { __preamble: message.content.slice(0, 120), ...toolCall.args };
        }
        const toolId = trackToolCall(toolCall);
        toolIds[toolCall.id] = toolId;
      });
      return toolIds;
    }

    // If no tool calls but the model emitted a plan, reflect that in UI briefly
    if (typeof message.content === 'string' && /\b(plan|planning)\b/i.test(message.content)) {
      setAgentThinking(message.content.slice(0, 180));
    }
    
    return {};
  };
  
  return {
    trackToolCall,
    trackToolResult,
    trackAgentMessage,
  };
}

// Helper to determine if a tool is backend or frontend
function isBackendTool(toolName: string): boolean {
  const backendTools = [
    'askFinancialAnalyst',
    'askDataSteward',
    'fetchMarketData',
    'searchSymbols',
    'calculatePortfolioRisk',
    'calculateTechnicals',
    'analyzeMarketSentiment',
    'getMarketSnapshot',
  ];
  
  return backendTools.includes(toolName);
}